import { ArrowRight, ArrowUpRight } from "lucide-react";
import ParallaxSection from "@/components/ParallaxSection";
import FlipGallery from "@/components/FlipGallery";
import PixelSlider from "@/components/PixelSlider";
import BookingFields from "@/components/BookingFields";
import { useBooking } from "@/context/BookingContext";
import { banquet2Img, IconGlyph } from "@/data";
import kitchenBgImg from "@/assets/dining/kitchen-bg.png";
import dayBannerImg from "@/assets/dining/day-banner.jpg";
import dayBanner2Img from "@/assets/dining/day-banner-2.jpg";
import dayBanner3Img from "@/assets/dining/day-banner-3.jpg";
import dayMorningImg from "@/assets/dining/day-morning.jpg";
import dayThroughImg from "@/assets/dining/day-through.jpg";
import dayEveningImg from "@/assets/dining/day-evening.jpg";
import diningBottomImg from "@/assets/dining/dining-bottom.png";
import diningHeroRooftopImg from "@/assets/dining/dining-hero-rooftop.png";
import setting1Img from "@/assets/dining/setting-1.png";
import setting2Img from "@/assets/dining/setting-2.png";
import setting3Img from "@/assets/dining/setting-3.png";
import setting4Img from "@/assets/dining/setting-4.png";
import setting5Img from "@/assets/dining/setting-5.png";
import barBgImg from "@/assets/dining/bar-bg.png";

const barWineImg = "https://images.unsplash.com/photo-1553361371-9b22f78e8b1d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const barWhiskeyImg = "https://images.unsplash.com/photo-1470337458703-46ad1756a187?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const barBottlesImg = "https://images.unsplash.com/photo-1779080415834-c13f4c682dc8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const cuisineContinentalImg = "https://images.unsplash.com/photo-1663530761401-15eefb544889?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const cuisineIndianImg = "https://images.unsplash.com/photo-1565557623262-b51c2513a641?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const cuisinePanAsianImg = "https://images.unsplash.com/photo-1607328874071-45a9cd600644?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const cuisines = [
  { name: "Continental", desc: "Refined European classics, plated with modern precision.", img: cuisineContinentalImg },
  { name: "North Indian", desc: "Slow-cooked curries and tandoor classics, rich with spice.", img: cuisineIndianImg },
  { name: "Pan-Asian", desc: "Bold, fresh flavours from across Asia — woks, broths and noodles.", img: cuisinePanAsianImg },
  { name: "Garo & Khasi Specialties", desc: "Local Meghalayan dishes, prepared the way the hills have always cooked.", img: setting1Img },
];

const plateHeroImg = "https://images.unsplash.com/photo-1761315600943-d8a5bb0c499f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const menuQuickImg = "https://images.unsplash.com/photo-1742593236678-c102b724c102?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
const reservationQuickImg = "https://images.unsplash.com/photo-1666600638841-4fe4a01ae260?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
const signatureFishImg = "https://images.unsplash.com/photo-1514326640560-7d063ef2aed5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const signatureSteakImg = "https://images.unsplash.com/photo-1600891964092-4316c288032e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const signatureDessertImg = "https://images.unsplash.com/photo-1548741448-9f4e87b7f7ab?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const signatureScallopImg = "https://images.unsplash.com/photo-1661874854804-0f89345cf432?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const signatureDishes = [
  {
    name: "Seared Scallop, Beurre Blanc",
    desc: "A delicate starter — pan-seared scallop, citrus beurre blanc, finished tableside.",
    facts: ["Chef's recommendation", "Ask about today's catch", "Pairs well with a dry white"],
    img: signatureScallopImg,
  },
  {
    name: "Char-Grilled Steak Frites",
    desc: "A char-grilled cut, rested and sliced, served with hand-cut fries and herb butter.",
    facts: ["Grilled to your liking", "Hand-cut, twice-fried potatoes", "Finished with herb butter"],
    img: signatureSteakImg,
  },
  {
    name: "Pan-Seared Reef Fish",
    desc: "The day's catch, pan-seared and finished with a light citrus dressing and seasonal greens.",
    facts: ["Sourced fresh, daily", "Light citrus dressing", "Gluten-free"],
    img: signatureFishImg,
  },
  {
    name: "Chef's Chocolate Selection",
    desc: "A hand-selected assortment from our pastry kitchen, finished with edible gold leaf.",
    facts: ["Made in-house, daily", "A rotating selection", "Best enjoyed with coffee"],
    img: signatureDessertImg,
  },
];

const kitchenHighlights = [
  { label: "100% Fresh Ingredients", icon: "housekeeping" },
  { label: "Experienced Chefs", icon: "housekeeping" },
  { label: "Rooted in the Hills", icon: "housekeeping" },
  { label: "Served with Care", icon: "housekeeping" },
];

const kitchenPoints = [
  { label: "Professional & Skilled Chefs", icon: "housekeeping" },
  { label: "Multi-Cuisine Kitchen", icon: "housekeeping" },
  { label: "Fresh, Local Ingredients", icon: "housekeeping" },
  { label: "Rooted in Garo Hills Flavours", icon: "housekeeping" },
  { label: "Every Dish Made to Order", icon: "housekeeping" },
  { label: "Attentive, Personal Service" , icon: "housekeeping" },
  { label: "Made Fresh Daily", icon: "housekeeping" },
  { label: "Private Dining Available", icon: "housekeeping" },
];

const dayParts = [
  { title: "Morning", desc: "Start unhurried, with a breakfast that sets an easy pace for the day ahead.", img: dayMorningImg },
  { title: "Through the Day", desc: "A relaxed space for a quiet coffee, a working lunch, or a pause between exploring Tura.", img: dayThroughImg },
  { title: "Evening", desc: "As the light softens, The Orchid Terrace settles into its evening rhythm — good food, good company, nothing rushed.", img: dayEveningImg },
];

export default function Dining() {
  const { openBooking } = useBooking();

  return (
    <>
      <ParallaxSection image={diningHeroRooftopImg} className="page-hero dn-hero" strength={16}>
        <div className="dn-hero-text">
          <p className="page-hero-eyebrow">
            <ArrowRight size={13} /> Dining
          </p>
          <h1>
            The Orchid <em>Terrace</em>
          </h1>
          <p className="page-hero-sub">Where local ingredients meet refined simplicity.</p>
        </div>

        <aside className="booking-card fade-up" data-testid="dining-booking-panel">
          <div className="booking-card-head">
            <h3>Booking Online</h3>
            <p>Check availability in seconds.</p>
          </div>
          <BookingFields onSubmit={openBooking} idPrefix="dining" />
        </aside>
      </ParallaxSection>

      {/* KITCHEN INTRO — rotating plate, quick links to Menu / Reservation / Events */}
      <section className="dn-intro">
        <div className="dn-intro-plate fade-up">
          <div className="dn-intro-plate-spin">
            <img src={plateHeroImg} alt="A signature plate at The Orchid Terrace" />
          </div>
        </div>
        <div className="dn-intro-copy fade-up">
          <p className="dn-intro-eyebrow">Eat. Linger. Return.</p>
          <h2>
            Flavours rooted <em>in the hills.</em>
          </h2>
          <p>
            The Orchid Terrace's kitchen moves easily across Continental, North Indian, Pan-Asian and local Garo &amp; Khasi cuisines — every dish made to order, with ingredients drawn from the Garo Hills whenever the season allows.
          </p>
          <p className="dn-intro-cta-line">
            Planning an event? <a href="#book-now" onClick={openBooking}>Contact us now</a>.
          </p>
        </div>
        <div className="dn-intro-links">
          <div className="dn-intro-links-row">
            <a href="#book-now" onClick={openBooking} className="dn-intro-link dn-intro-link-menu">
              <img src={menuQuickImg} alt="View the menu at The Orchid Terrace" loading="lazy" />
              <span>Menu</span>
            </a>
            <a href="#book-now" onClick={openBooking} className="dn-intro-link dn-intro-link-reservation">
              <img src={reservationQuickImg} alt="Reserve a table at The Orchid Terrace" loading="lazy" />
              <span>Reservation</span>
            </a>
          </div>
          <div className="dn-intro-links-row">
            <a href="#book-now" onClick={openBooking} className="dn-intro-link dn-intro-link-events">
              <img src={banquet2Img} alt="Events and celebrations at The Vanda" loading="lazy" />
              <span>Events</span>
            </a>
          </div>
        </div>
      </section>

      {/* A DAY AT THE TABLE */}
      <section className="day-section">
        <div className="day-header">
          <div className="day-header-head fade-up">
            <p className="eyebrow">
              <ArrowRight size={13} /> The Daily Rhythm
            </p>
            <h2>
              A day<br />at the <em>table.</em>
            </h2>
          </div>
          <div className="day-header-copy fade-up">
            <p>
              The Orchid Terrace is The Vanda's in-house restaurant — a space to sit back and enjoy good food, thoughtfully prepared through the day. From a quick breakfast to a relaxed dinner after a day out in Tura, it brings the same quiet precision to the table that shapes the rest of the stay.
            </p>
          </div>
        </div>

        <div className="day-banner fade-up">
          <PixelSlider
            images={[
              { src: dayBannerImg, alt: "Table set at The Orchid Terrace" },
              { src: dayBanner2Img, alt: "Dining area at The Orchid Terrace" },
              { src: dayBanner3Img, alt: "Seating at The Orchid Terrace" },
            ]}
            cycleSeconds={5}
          />
        </div>

        <div className="day-timeline" aria-hidden="true">
          <div className="day-timeline-track">
            <span className="day-timeline-dot" style={{ left: "16.6667%" }} />
            <span className="day-timeline-dot" style={{ left: "50%" }} />
            <span className="day-timeline-dot" style={{ left: "83.3333%" }} />
          </div>
        </div>

        <div className="day-grid">
          {dayParts.map((d, i) => (
            <div className="day-card fade-up" key={d.title} style={{ transitionDelay: `${i * 0.08}s` }}>
              <img src={d.img} alt={`${d.title} at The Orchid Terrace`} className="day-card-img" />
              <h4>{d.title}</h4>
              <p>{d.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CUISINES WE SERVE — the kitchen's range, a key revenue driver */}
      <section className="dn-cuisine" id="cuisines">
        <div className="dn-cuisine-head fade-up">
          <p className="eyebrow">
            <ArrowRight size={13} /> Our Kitchen
          </p>
          <h2>
            A table for <em>every craving.</em>
          </h2>
          <p className="dn-cuisine-sub">
            From refined Continental plates to the bold spice of the hills — The Orchid Terrace's kitchen moves easily across cuisines, without ever losing its precision.
          </p>
        </div>
        <div className="dn-cuisine-grid">
          {cuisines.map((c) => (
            <article className="dn-cuisine-card fade-up" key={c.name}>
              <div className="dn-cuisine-media">
                <img src={c.img} alt={`${c.name} at The Orchid Terrace`} loading="lazy" />
              </div>
              <h4>{c.name}</h4>
              <p>{c.desc}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ROOTED IN PLACE — kitchen background */}
      <section className="dn-kitchen-band" style={{ backgroundImage: `url(${kitchenBgImg})` }}>
        <div className="split-band-copy fade-up">
          <p className="eyebrow">
            <ArrowRight size={13} /> Rooted in Place
          </p>
          <h2>
            Refined, <em>never excessive.</em>
          </h2>
          <p>
            The kitchen draws on local ingredients and the flavours of the Garo Hills, prepared with the same restraint that defines every corner of The Vanda.
          </p>
        </div>
      </section>

      {/* THE KITCHEN, IN SHORT — closing statement on what makes it different */}
      <section className="dn-kitchen-points">
        <div className="dn-kitchen-points-media fade-up">
          <img src={setting3Img} alt="Plated fish course with a glass of wine at The Orchid Terrace" />
        </div>
        <div className="dn-kitchen-points-copy fade-up">
          <p className="eyebrow light">
            <ArrowRight size={13} /> Simple. Honest. Considered.
          </p>
          <h2>
            A taste of sophistication, <em>in the heart of the town.</em>
          </h2>
          <p>
            Every dish at The Orchid Terrace starts with the same intent — good ingredients, treated with care, served without fuss. It's a kitchen that moves across cuisines comfortably, but never forgets the hills it calls home.
          </p>
          <ul className="dn-kitchen-points-list">
            {kitchenPoints.map((k) => (
              <li key={k.label}>
                <IconGlyph name={k.icon} /> <span>{k.label}</span>
              </li>
            ))}
          </ul>
          <a href="#cuisines" className="btn btn-outline light" data-testid="dining-view-cuisines">
            Explore Our Cuisines <ArrowRight size={15} />
          </a>
        </div>
      </section>

      {/* SIGNATURE DISHES — hover to reveal each dish's story */}
      <section className="dn-signature">
        <div className="dn-signature-head fade-up">
          <div>
            <p className="eyebrow">
              <ArrowRight size={13} /> Signature Dishes
            </p>
            <h2>
              A few of our <em>chef's favourites.</em>
            </h2>
          </div>
          <div className="dn-signature-badges">
            {kitchenHighlights.map((k) => (
              <span key={k.label} className="dn-signature-badge">
                <IconGlyph name={k.icon} /> {k.label}
              </span>
            ))}
          </div>
        </div>
        <div className="dn-signature-row fade-up">
          {signatureDishes.map((d) => (
            <div className="dn-signature-card" key={d.name} tabIndex={0}>
              <div className="dn-signature-card-img">
                <img src={d.img} alt={d.name} loading="lazy" />
              </div>
              <div className="dn-signature-card-panel">
                <h4>{d.name}</h4>
                <p>{d.desc}</p>
                <ul className="dn-signature-facts">
                  {d.facts.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
                <a href="#book-now" onClick={openBooking} className="dn-signature-cta">
                  Reserve a Table <ArrowRight size={14} />
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* AMBIANCE — flip gallery, brand-green band */}
      <div className="split-band forest compact">
        <div className="split-band-media">
          <FlipGallery
            images={[
              { src: setting1Img, alt: "Garo thali at The Orchid Terrace" },
              { src: setting2Img, alt: "A dish being flambéed in the kitchen" },
              { src: setting3Img, alt: "Plated fish course with a glass of wine" },
              { src: setting4Img, alt: "Rooftop bar seating at dusk" },
              { src: setting5Img, alt: "A drink being served at the bar" },
            ]}
            cycleSeconds={25}
          />
        </div>
        <div className="split-band-copy fade-up">
          <p className="eyebrow">
            <ArrowRight size={13} /> The Setting
          </p>
          <h2>
            A space to sit back, <em>and stay a while.</em>
          </h2>
          <p>
            Warm materials, generous windows and calm interiors — the same considered design language that shapes every room at The Vanda, brought to the table.
          </p>
        </div>
      </div>

      {/* THE VANDA BAR — craft cocktails, rooftop setting */}
      <section className="dn-bar" style={{ backgroundImage: `url(${barBgImg})` }}>
        <div className="dn-bar-overlay" aria-hidden="true" />
        <div className="dn-bar-inner">
          <div className="dn-bar-copy fade-up">
            <p className="eyebrow light center">
              <ArrowRight size={13} /> The Vanda Bar
            </p>
            <h2>
              Where the evening <em>slows down.</em>
            </h2>
            <p>
              Perched above the town, The Vanda Bar pairs a curated list of spirits and craft cocktails with sweeping views of the Garo Hills — a fitting close to a day in Tura, or a warm start to the evening ahead.
            </p>
            <a href="#book-now" onClick={openBooking} className="btn btn-outline light" data-testid="bar-cta">
              Reserve a Table <ArrowRight size={15} />
            </a>
          </div>
          <div className="dn-bar-collage fade-up">
            <img src={setting4Img} alt="Rooftop bar seating at The Vanda, at dusk" loading="lazy" />
            <img src={barWineImg} alt="Wine being poured at The Vanda Bar" loading="lazy" />
            <img src={barWhiskeyImg} alt="A whiskey being poured over ice at The Vanda Bar" loading="lazy" />
            <img src={barBottlesImg} alt="Single malts and spirits behind The Vanda Bar" loading="lazy" />
          </div>
        </div>
      </section>

      <section
        className="inner-cta-band dn-cta"
        style={{ backgroundImage: `linear-gradient(rgba(10,9,8,0.48), rgba(10,9,8,0.48)), url(${diningBottomImg})` }}
      >
        <p className="eyebrow">
          <ArrowRight size={13} /> Reserve a table
        </p>
        <h2>
          Good food, <em>good company.</em>
        </h2>
        <button onClick={openBooking} className="btn btn-primary large" data-testid="dining-cta-enquire">
          Enquire Now <ArrowUpRight size={17} />
        </button>
      </section>
    </>
  );
}
